import type { RevenueCommissionEntry, RevenuePayoutItem, RevenuePayoutRecord } from "../../types/revenueOps";

export type PayoutPreparationEntry = RevenueCommissionEntry & {
  payout_readiness: "ready" | "blocked";
  payout_reason?: string;
};

export type PayoutPreparationStatusFilter = "available" | "batched" | "blocked" | "all";
export type PayoutPreparationSort = "newest" | "oldest" | "payee_asc" | "amount_desc" | "amount_asc" | "account_asc";
export type PayoutPayeeRollup = { partner_id?: string; partner_name: string; available_count: number; available_total: number; selected_count: number; selected_total: number };

export function derivePayoutPreparationEntries(
  entries: RevenueCommissionEntry[],
  payoutItems: RevenuePayoutItem[],
  activePartnerIds: Set<string>
): PayoutPreparationEntry[] {
  const batched = new Set(payoutItems.map((item) => item.commission_entry_id));
  return entries
    .filter((entry) => (entry.source_status === "approved" || entry.status === "approved") && ["approved", "reviewed"].includes(String(entry.review_status || "approved").toLowerCase()))
    .map((entry) => {
      if (batched.has(entry.id)) return { ...entry, payout_readiness: "blocked", payout_reason: "Already included in a payout batch." };
      if (!entry.partner_id) return { ...entry, payout_readiness: "blocked", payout_reason: "Missing partner linkage." };
      if (!activePartnerIds.has(entry.partner_id)) return { ...entry, payout_readiness: "blocked", payout_reason: "Partner is not active." };
      if (!Number.isFinite(entry.commission_amount) || entry.commission_amount <= 0) return { ...entry, payout_readiness: "blocked", payout_reason: "Commission amount must be greater than zero." };
      if (entry.status === "manual_hold" || entry.status === "void" || entry.status === "paid") return { ...entry, payout_readiness: "blocked", payout_reason: "Entry is not eligible for payout preparation." };
      return { ...entry, payout_readiness: "ready" };
    });
}

export function validatePayoutSelection(entries: PayoutPreparationEntry[], selectedIds: string[]) {
  if (!selectedIds.length) return "Select at least one approved commission entry.";
  const selected = entries.filter((entry) => selectedIds.includes(entry.id));
  if (selected.length !== selectedIds.length) return "One or more selected commissions are no longer available for payout preparation.";
  const blocked = selected.find((entry) => entry.payout_readiness !== "ready");
  if (blocked) return blocked.payout_reason || "Only approved commissions that are not already in a payout batch can be selected.";
  return null;
}

export function filterAndSortPayoutPreparationEntries(entries: PayoutPreparationEntry[], payeeId: string, status: PayoutPreparationStatusFilter, sort: PayoutPreparationSort) {
  const filtered = entries.filter((entry) => {
    const payeeMatches = !payeeId || entry.partner_id === payeeId;
    const statusMatches = status === "all" || (status === "available" && entry.payout_readiness === "ready") || (status === "blocked" && entry.payout_readiness === "blocked" && !entry.payout_reason?.startsWith("Already included")) || (status === "batched" && entry.payout_reason?.startsWith("Already included"));
    return payeeMatches && statusMatches;
  });
  return [...filtered].sort((a, b) => {
    if (sort === "payee_asc") return a.partner_name.localeCompare(b.partner_name) || a.customer_account.localeCompare(b.customer_account);
    if (sort === "amount_desc") return b.commission_amount - a.commission_amount;
    if (sort === "amount_asc") return a.commission_amount - b.commission_amount;
    if (sort === "account_asc") return a.customer_account.localeCompare(b.customer_account);
    const aDate = Date.parse(a.approved_at || a.created_at || "") || 0;
    const bDate = Date.parse(b.approved_at || b.created_at || "") || 0;
    return sort === "oldest" ? aDate - bDate : bDate - aDate;
  });
}

export function summarizePayoutPayees(entries: PayoutPreparationEntry[], selectedIds: string[]): PayoutPayeeRollup[] {
  const rollups = new Map<string, PayoutPayeeRollup>();
  entries.filter((entry) => entry.payout_readiness === "ready").forEach((entry) => {
    const key = entry.partner_id || entry.partner_name;
    const current = rollups.get(key) || { partner_id: entry.partner_id, partner_name: entry.partner_name, available_count: 0, available_total: 0, selected_count: 0, selected_total: 0 };
    current.available_count += 1;
    current.available_total += entry.commission_amount;
    if (selectedIds.includes(entry.id)) {
      current.selected_count += 1;
      current.selected_total += entry.commission_amount;
    }
    rollups.set(key, current);
  });
  return [...rollups.values()].sort((a, b) => a.partner_name.localeCompare(b.partner_name));
}

export function summarizePayoutBatchItems(items: RevenuePayoutItem[]) {
  return { item_count: items.length, total_amount: items.reduce((total, item) => total + item.amount, 0), partner_count: new Set(items.map((item) => item.partner_id)).size };
}

export function buildPayoutBatchDraft(selected: PayoutPreparationEntry[], now = new Date()): Pick<RevenuePayoutRecord, "amount" | "status"> & { payout_period_start: string; payout_period_end: string; item_count: number; partner_count: number } {
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const isoDate = (date: Date) => date.toISOString().slice(0, 10);
  return {
    payout_period_start: isoDate(start),
    payout_period_end: isoDate(end),
    amount: selected.reduce((total, entry) => total + entry.commission_amount, 0),
    item_count: selected.length,
    partner_count: new Set(selected.map((entry) => entry.partner_id).filter(Boolean)).size,
    status: "draft",
  };
}
